import { FC, MouseEventHandler, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { SVG_MUI } from "@ryde/assets";
import { useTableFiltersActions } from "@ryde/redux";
import { Log, stringifyDropdownItems, useIsMobile } from "@ryde/shared";

import { FramerTooltip } from "../../common";
import { MenuButton } from "../../controls";
import { useFilterMultiselectUnbound } from "./hooks";
import {
  FilterDropdownWrapper,
  IFilterMultiProps,
  selectStyles,
  selectStylesMobile,
  StyledLabelBlack,
  StyledLabelBlue,
} from "./parts";
import { MemoSelect } from "./parts/memo-select";

export const FilterMultiselectUnbound: FC<IFilterMultiProps> = props => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const {
    isOpen,
    setIsOpen,
    value,
    buttonContent,
    multiselectLabel,
    onItemsCheckChange,
    dropdownItems,
    selectAll,
    isSelectedAll,
    input,
    onInputChange,
    filteredDropDownItems,
  } = useFilterMultiselectUnbound(props);
  const { customFieldId } = props;
  const handleToggle = useEvent(() => setIsOpen(prev => !prev));
  const handleClose = useEvent(() => setIsOpen(false));
  const { handleRemoveFilter } = useTableFiltersActions();
  const removeFilter: MouseEventHandler = useEvent(e => {
    e.stopPropagation();
    handleClose();

    if (customFieldId) {
      handleRemoveFilter(+customFieldId);
    } else {
      Log.warn(t("errors.cantRemoveFilterInvalidFieldId"));
    }
  });

  const stylesComputed = useMemo(() => {
    return isMobile ? selectStylesMobile : selectStyles;
  }, [isMobile]);

  const tooltipText =
    value.length === 0
      ? t("general.FILTERED_BY_ENTITY", { entity: multiselectLabel })
      : t("general.FILTERED_BY_ENTITY", { entity: multiselectLabel }) +
        " : " +
        stringifyDropdownItems(value);

  return (
    <FilterDropdownWrapper
      dropdownHasSelectedItems={!!value?.length}
      isFilterItem={true}
      isOpen={isOpen}
      onClose={handleClose}
      target={
        <FramerTooltip content={tooltipText}>
          <MenuButton
            onClick={handleToggle}
            text={multiselectLabel}
            labelBlack={buttonContent.labelBlack}
            labelBlue={buttonContent.labelBlue}
            imageAfterText
            disabled={!dropdownItems.length}
          >
            <StyledLabelBlack>{buttonContent.labelBlack}</StyledLabelBlack>
            <StyledLabelBlue>{buttonContent.labelBlue}</StyledLabelBlue>
            <SVG_MUI.CircleXMark onClick={e => removeFilter(e)} />
          </MenuButton>
        </FramerTooltip>
      }
    >
      <MemoSelect
        value={value}
        selectAll={selectAll}
        isSelectedAll={isSelectedAll}
        filteredDropDownItems={filteredDropDownItems}
        dropdownItems={dropdownItems}
        onItemsCheckChange={onItemsCheckChange}
        stylesComputed={stylesComputed}
        input={input}
        onInputChange={onInputChange}
        isSelectNoneOptionEnabled={false}
      />
    </FilterDropdownWrapper>
  );
};
